/**
 * NFL stat columns, formatters and per-position stat lines.
 *
 * The NFL mirror of `@/components/mlb-beta/statsTableDefs` — same shape, football
 * semantics. Shared by My Team (desktop grid + mobile cards), Players, and the player card
 * so the four surfaces cannot drift on what a column means or how a zero renders.
 *
 * ── TWO SOURCES, ON PURPOSE ─────────────────────────────────────────────────
 *
 * A row's SAB PTS / TRS / ANT come from `nfl_player_match_scores` (per-league, includes the
 * sabotage card bonus — what the league actually scored, and what the week header sums).
 * Its stat CATEGORIES come from `nfl_player_game_stats` (global, league-independent),
 * filtered to the same weeks.
 *
 * These are different tables at different grain and there is no single source that carries
 * both. Do NOT "fix" this into one read: collapsing to game_stats loses the league's card
 * bonus, and collapsing to match_scores loses every raw stat. They reconcile per game
 * because they describe the same player-week; they are not the same rows.
 *
 * ── STAT KEYS ARE VERIFIED AGAINST PROD, NOT ASSUMED ────────────────────────
 *
 * Three of the handoff's key paths were wrong and are corrected here:
 *   - DST reads `dst.*`, not `defense.*`. `defense` is the INDIVIDUAL defender category
 *     (653 rows on 2026 REG wk1) and carries no points_allowed / yards_allowed at all.
 *     `dst` (30 rows) is team defense and has all four values the line needs.
 *   - COACH penalties are `coach.team_penalties`, not `penalties.penalties`.
 *   - COACH points allowed is `coach.opponent_score`.
 */

import type { NflRange } from '@/lib/nflWeekRange';

export type { NflRange };

/** The handoff's VIEW control. `game` divides stats AND points by games played. */
export type NflStatView = 'total' | 'game';

export const NFL_VIEW_OPTIONS: { value: NflStatView; label: string }[] = [
  { value: 'total', label: 'Totals' },
  { value: 'game', label: 'Per Game' },
];

// ── raw categories ──────────────────────────────────────────────────────────

/** Every stat path the four surfaces read, flattened into one summable shape. */
export interface NflRawStats {
  pass_att: number; pass_cmp: number; pass_yds: number; pass_td: number; pass_int: number; pass_sk: number;
  rush_att: number; rush_yds: number; rush_td: number; rush_tlost: number;
  rec: number; tgt: number; rec_yds: number; rec_td: number; drops: number;
  fumbles: number;
  penalties: number;
  fg_made: number; fg_att: number; fg_blk: number;
  punts: number; punts_in20: number; punts_tb: number;
  dst_pa: number; dst_yda: number; dst_sk: number; dst_int: number;
  coach_pen: number; coach_pa: number;
}

export const EMPTY_RAW_STATS: NflRawStats = {
  pass_att: 0, pass_cmp: 0, pass_yds: 0, pass_td: 0, pass_int: 0, pass_sk: 0,
  rush_att: 0, rush_yds: 0, rush_td: 0, rush_tlost: 0,
  rec: 0, tgt: 0, rec_yds: 0, rec_td: 0, drops: 0,
  fumbles: 0,
  penalties: 0,
  fg_made: 0, fg_att: 0, fg_blk: 0,
  punts: 0, punts_in20: 0, punts_tb: 0,
  dst_pa: 0, dst_yda: 0, dst_sk: 0, dst_int: 0,
  coach_pen: 0, coach_pa: 0,
};

/** Every raw key and the prod jsonb path it reads. The only place these paths appear. */
const STAT_PATHS: Record<keyof NflRawStats, [string, string]> = {
  pass_att:   ['passing', 'attempts'],       pass_cmp:  ['passing', 'completions'],
  pass_yds:   ['passing', 'yards'],          pass_td:   ['passing', 'touchdowns'],
  pass_int:   ['passing', 'interceptions'],  pass_sk:   ['passing', 'sacks'],
  rush_att:   ['rushing', 'attempts'],       rush_yds:  ['rushing', 'yards'],
  rush_td:    ['rushing', 'touchdowns'],     rush_tlost:['rushing', 'tlost'],
  rec:        ['receiving', 'receptions'],   tgt:       ['receiving', 'targets'],
  rec_yds:    ['receiving', 'yards'],        rec_td:    ['receiving', 'touchdowns'],
  drops:      ['receiving', 'dropped_passes'],
  fumbles:    ['fumbles', 'fumbles'],
  penalties:  ['penalties', 'penalties'],
  fg_made:    ['field_goals', 'made'],       fg_att:    ['field_goals', 'attempts'],
  fg_blk:     ['field_goals', 'blocked'],
  punts:      ['punts', 'attempts'],         punts_in20:['punts', 'inside_20'],
  punts_tb:   ['punts', 'touchbacks'],
  // Team defense, NOT the individual-defender `defense` category — see the header note.
  dst_pa:     ['dst', 'points_allowed'],     dst_yda:   ['dst', 'yards_allowed'],
  dst_sk:     ['dst', 'sacks'],              dst_int:   ['dst', 'interceptions'],
  coach_pen:  ['coach', 'team_penalties'],   coach_pa:  ['coach', 'opponent_score'],
};

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Pull one game's jsonb into the flat shape. Missing categories read 0, never NaN. */
export function readRawStats(stats: unknown): NflRawStats {
  const out = { ...EMPTY_RAW_STATS };
  if (!stats || typeof stats !== 'object') return out;
  const s = stats as Record<string, unknown>;
  for (const key of Object.keys(STAT_PATHS) as (keyof NflRawStats)[]) {
    const [cat, field] = STAT_PATHS[key];
    const bucket = s[cat];
    if (bucket && typeof bucket === 'object') out[key] = num((bucket as Record<string, unknown>)[field]);
  }
  return out;
}

export function addRawStats(a: NflRawStats, b: NflRawStats): NflRawStats {
  const out = { ...EMPTY_RAW_STATS };
  for (const key of Object.keys(EMPTY_RAW_STATS) as (keyof NflRawStats)[]) out[key] = a[key] + b[key];
  return out;
}

/** One player's numbers for the selected range, from both sources. */
export interface NflStatRow {
  gamesPlayed: number;
  raw: NflRawStats;
  /** From nfl_player_match_scores. null = no scored row in range (render "--", not 0). */
  trash: number | null;
  anti: number | null;
  /** trash + anti + sabotage card bonus — the league's number. See SAB-809. */
  sab: number | null;
}

// ── formatters ──────────────────────────────────────────────────────────────

/**
 * Counting stats: the dash check applies AFTER rounding, per `fmtInt` in statsTableDefs.
 * 0.4 per game displays as `-`, not `0`, because `0` claims a measurement that rounding
 * destroyed.
 */
export const fmtInt = (v: number): string => (Math.round(v) === 0 ? '–' : String(Math.round(v)));

/** Per-game counting stats — one decimal, same dash-after-rounding rule. */
export const fmtPG = (v: number): string => (Number(v.toFixed(1)) === 0 ? '–' : v.toFixed(1));

/**
 * Points (SAB / TRS / ANT) deliberately do NOT take the dash rule: a genuine 0.0 score is a
 * claim the engine made, unlike a stat that rounded away. Absence is `null`, which renders
 * "--". This mirrors MLB's getSabPtsDisplay exactly.
 */
export const fmtPts = (v: number): string => (v >= 0 ? `+${v.toFixed(1)}` : v.toFixed(1));

/** Stat value for a cell, honouring the view. GP is never divided. */
export function getStatDisplay(value: number, view: NflStatView, gamesPlayed: number, isGP = false): string {
  if (isGP) return String(Math.round(value));
  if (view === 'game') {
    if (gamesPlayed <= 0) return '–';
    return fmtPG(value / gamesPlayed);
  }
  return fmtInt(value);
}

/**
 * Points for a cell, honouring the view.
 *
 * Per Game divides the POINTS too. A row showing per-game stats beside season-total points
 * was a review bug; the two must move together or the row is lying about what it measures.
 */
export function getSabPtsDisplay(pts: number | null | undefined, view: NflStatView, gamesPlayed: number): string {
  if (pts == null) return '--';
  if (view === 'game') {
    if (gamesPlayed <= 0) return '--';
    return fmtPts(pts / gamesPlayed);
  }
  return fmtPts(pts);
}

/** Scale a raw stat the same way the display does, for the stat line. */
export function scaleStat(value: number, view: NflStatView, gamesPlayed: number): number {
  if (view === 'game') return gamesPlayed > 0 ? value / gamesPlayed : 0;
  return value;
}

// ── columns ─────────────────────────────────────────────────────────────────

/**
 * Header tone. Trash stats score FOR you (damage red), anti stats score AGAINST you
 * (success green), GP is neutral. This is the one place the inverted model is visible at a
 * glance, so the sort a manager wants is findable by colour.
 */
export type NflStatTone = 'trash' | 'anti' | 'neutral';

export const TONE_CLASS: Record<NflStatTone, string> = {
  trash: 'text-damage',
  anti: 'text-success',
  neutral: 'text-muted-foreground',
};

export interface NflStatColumn {
  key: string;
  label: string;
  tone: NflStatTone;
  /** Tailwind width class — every column but PLAYER is fixed so the grid cannot overflow. */
  width: string;
  get: (row: NflStatRow) => number;
  isGP?: boolean;
}

/** My Team's six shared columns — summed across categories so one set works per position. */
export const MY_TEAM_STAT_COLUMNS: NflStatColumn[] = [
  { key: 'GP',  label: 'GP',  tone: 'neutral', width: 'w-[42px]', isGP: true, get: r => r.gamesPlayed },
  { key: 'YDS', label: 'YDS', tone: 'anti',    width: 'w-[42px]', get: r => r.raw.pass_yds + r.raw.rush_yds + r.raw.rec_yds },
  { key: 'TD',  label: 'TD',  tone: 'anti',    width: 'w-[42px]', get: r => r.raw.pass_td + r.raw.rush_td + r.raw.rec_td },
  { key: 'TO',  label: 'TO',  tone: 'trash',   width: 'w-[42px]', get: r => r.raw.pass_int + r.raw.fumbles },
  { key: 'SK',  label: 'SK',  tone: 'trash',   width: 'w-[42px]', get: r => r.raw.pass_sk },
  { key: 'PEN', label: 'PEN', tone: 'trash',   width: 'w-[42px]', get: r => r.raw.penalties },
];

/** Players' six stat keys — how a manager finds trash production to claim. */
export const PLAYERS_STAT_COLUMNS: NflStatColumn[] = [
  { key: 'GP',  label: 'GP',  tone: 'neutral', width: 'w-[46px]', isGP: true, get: r => r.gamesPlayed },
  { key: 'YDS', label: 'YDS', tone: 'anti',    width: 'w-[46px]', get: r => r.raw.pass_yds + r.raw.rush_yds + r.raw.rec_yds },
  { key: 'TD',  label: 'TD',  tone: 'anti',    width: 'w-[46px]', get: r => r.raw.pass_td + r.raw.rush_td + r.raw.rec_td },
  { key: 'INT', label: 'INT', tone: 'trash',   width: 'w-[46px]', get: r => r.raw.pass_int },
  { key: 'FUM', label: 'FUM', tone: 'trash',   width: 'w-[46px]', get: r => r.raw.fumbles },
  { key: 'DRP', label: 'DRP', tone: 'trash',   width: 'w-[46px]', get: r => r.raw.drops },
  { key: 'TFL', label: 'TFL', tone: 'trash',   width: 'w-[46px]', get: r => r.raw.rush_tlost },
];

// ── stat line ───────────────────────────────────────────────────────────────

/**
 * The per-position sentence under a player's name.
 *
 * This is what makes a K / P / DST / COACH row readable when the six shared columns are all
 * dashes for them, so it is not optional decoration. Built from range- and view-scaled raw
 * values, never from the displayed column text.
 */
export function buildStatLine(position: string | null | undefined, row: NflStatRow, view: NflStatView): string {
  const gp = row.gamesPlayed;
  const v = (n: number) => {
    const scaled = scaleStat(n, view, gp);
    return view === 'game' ? Number(scaled.toFixed(1)) : Math.round(scaled);
  };
  const r = row.raw;
  const pos = (position ?? '').toUpperCase();

  switch (pos) {
    case 'QB':
      return `${v(r.pass_cmp)}/${v(r.pass_att)} CMP, ${v(r.pass_yds)} YD, ${v(r.pass_td)} TD, ${v(r.pass_int)} INT, ${v(r.pass_sk)} SK`;
    case 'RB':
    case 'FB':
      return `${v(r.rush_att)} CAR, ${v(r.rush_yds)} YD, ${v(r.rush_td)} TD, ${v(r.rec)}/${v(r.tgt)} REC, ${v(r.rec_yds)} YD`;
    case 'WR':
    case 'TE':
      return `${v(r.rec)}/${v(r.tgt)} REC, ${v(r.rec_yds)} YD, ${v(r.rec_td)} TD, ${v(r.drops)} DRP`;
    case 'K':
      return `${v(r.fg_made)}/${v(r.fg_att)} FG, ${v(r.fg_blk)} BLK`;
    case 'P':
      return `${v(r.punts)} PUNT, ${v(r.punts_in20)} IN20, ${v(r.punts_tb)} TB`;
    case 'DST':
    case 'D/ST':
      return `${v(r.dst_pa)} PA, ${v(r.dst_yda)} YD ALW, ${v(r.dst_sk)} SK, ${v(r.dst_int)} INT`;
    case 'COACH':
      return `${v(r.coach_pen)} TEAM PEN, ${v(r.coach_pa)} PA`;
    default:
      // IDP and anything else the pool grows: fall back to the shared shape rather than an
      // empty line, so a new position never renders a blank row.
      return `${v(r.rush_yds + r.rec_yds)} YD, ${v(r.rush_td + r.rec_td)} TD, ${v(r.fumbles)} FUM`;
  }
}
